import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';

// ─── OTP Input Component ────────────────────────────────────────────────────
const OtpInput = ({ value, onChange, disabled }) => {
  const inputsRef = useRef([]);
  const digits = (value || '').split('').concat(Array(6).fill('')).slice(0, 6);

  const handleKeyDown = (e, idx) => {
    if (e.key === 'Backspace') {
      e.preventDefault();
      const next = digits.map((d, i) => (i === idx ? '' : d));
      onChange(next.join(''));
      if (idx > 0) inputsRef.current[idx - 1]?.focus();
    } else if (e.key === 'ArrowLeft' && idx > 0) {
      inputsRef.current[idx - 1]?.focus();
    } else if (e.key === 'ArrowRight' && idx < 5) {
      inputsRef.current[idx + 1]?.focus();
    }
  };

  const handleChange = (e, idx) => {
    const raw = e.target.value.replace(/\D/g, '');
    if (!raw) {
      const next = digits.map((d, i) => (i === idx ? '' : d));
      onChange(next.join(''));
      return;
    }
    if (raw.length > 1) {
      const pasted = raw.slice(0, 6);
      onChange(pasted.padEnd(6, '').slice(0, 6));
      inputsRef.current[Math.min(pasted.length, 5)]?.focus();
      return;
    }
    const next = digits.map((d, i) => (i === idx ? raw : d));
    onChange(next.join(''));
    if (idx < 5) inputsRef.current[idx + 1]?.focus();
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    onChange(pasted.padEnd(6, '').slice(0, 6));
    inputsRef.current[Math.min(pasted.length, 5)]?.focus();
  };

  return (
    <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
      {digits.map((d, idx) => (
        <input
          key={idx}
          ref={(el) => (inputsRef.current[idx] = el)}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={d}
          disabled={disabled}
          onChange={(e) => handleChange(e, idx)}
          onKeyDown={(e) => handleKeyDown(e, idx)}
          onPaste={handlePaste}
          className="otp-box"
          style={{
            border: `2px solid ${d ? '#3B6BE4' : '#E5E7EB'}`,
            background: d ? '#EEF2FF' : '#F9FAFB',
          }}
          onFocus={(e) => (e.target.style.borderColor = '#3B6BE4')}
          onBlur={(e) => (e.target.style.borderColor = d ? '#3B6BE4' : '#E5E7EB')}
        />
      ))}
    </div>
  );
};

// ─── Countdown Timer ────────────────────────────────────────────────────────
const useCountdown = (seconds) => {
  const [timeLeft, setTimeLeft] = useState(0);
  const intervalRef = useRef(null);
  const start = () => {
    setTimeLeft(seconds);
    clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      setTimeLeft((t) => {
        if (t <= 1) { clearInterval(intervalRef.current); return 0; }
        return t - 1;
      });
    }, 1000);
  };
  useEffect(() => () => clearInterval(intervalRef.current), []);
  return { timeLeft, start };
};

const isMobileNum = (val) => /^[6-9]\d{9}$/.test(val.trim());
const isEmail     = (val) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val.trim());

// ─── LoginScreen ─────────────────────────────────────────────────────────────
const LoginScreen = () => {
  const navigate = useNavigate();

  const [step, setStep]                           = useState('input'); // 'input' | 'otp'
  const [identifier, setIdentifier]               = useState('');
  const [identifierError, setIdentifierError]     = useState('');
  const [identifierTouched, setIdentifierTouched] = useState(false);
  const [resolvedEmail, setResolvedEmail]         = useState('');
  const [maskedEmail, setMaskedEmail]             = useState('');
  const [otp, setOtp]                             = useState('');
  const [otpError, setOtpError]                   = useState('');
  const [isLoading, setIsLoading]                 = useState(false);
  const [successMessage, setSuccessMessage]       = useState('');
  const [submitError, setSubmitError]             = useState('');

  const { timeLeft, start: startCountdown } = useCountdown(60);

  // ── Helpers ───────────────────────────────────────────────
  const maskEmail = (email) => {
    const [user, domain] = email.split('@');
    const masked = user[0] + '*'.repeat(Math.max(user.length - 2, 2)) + user[user.length - 1];
    return `${masked}@${domain}`;
  };

  const validateIdentifier = (val) => {
    if (!val?.trim()) return 'Email or mobile number is required';
    if (!isEmail(val) && !isMobileNum(val))
      return 'Enter a valid email address or 10-digit mobile number';
    return '';
  };

  // ── Handlers ──────────────────────────────────────────────
  const handleIdentifierChange = (e) => {
    setIdentifier(e.target.value);
    setSubmitError('');
    if (identifierTouched) setIdentifierError(validateIdentifier(e.target.value));
  };

  const handleIdentifierBlur = () => {
    setIdentifierTouched(true);
    setIdentifierError(validateIdentifier(identifier));
  };

  // ── Step 1: Send OTP ──────────────────────────────────────
  const handleSendOtp = async (e) => {
    e?.preventDefault();
    setIdentifierTouched(true);
    const err = validateIdentifier(identifier);
    setIdentifierError(err);
    if (err) return;

    setIsLoading(true);
    setSubmitError('');
    setSuccessMessage('');

    try {
      let emailToUse = '';

      if (isEmail(identifier)) {
        emailToUse = identifier.trim().toLowerCase();
      } else {
        // Mobile number — look up linked email from user_profiles
        const { data, error } = await supabase
          .from('user_profiles')
          .select('email')
          .eq('mobile', identifier.trim())
          .single();

        if (error || !data?.email) {
          setSubmitError('No account found with this mobile number.');
          setIsLoading(false);
          return;
        }
        emailToUse = data.email;
      }

      const { error: otpErr } = await supabase.auth.signInWithOtp({
        email: emailToUse,
        options: { shouldCreateUser: false },
      });

      if (otpErr) {
        if (otpErr.status === 429 || otpErr.message?.toLowerCase().includes('rate limit')) {
          setSubmitError('Too many requests. Please wait a moment and try again.');
        } else {
          setSubmitError(otpErr.message || 'Failed to send OTP. Please try again.');
        }
        return;
      }

      setResolvedEmail(emailToUse);
      setMaskedEmail(maskEmail(emailToUse));
      setStep('otp');
      startCountdown();
      setSuccessMessage(`A 6-digit code was sent to ${maskEmail(emailToUse)}`);
    } catch (err) {
      setSubmitError(err?.message || 'Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // ── Step 2: Verify OTP + Role-based redirect ──────────────
  const handleVerifyOtp = async (e) => {
    e?.preventDefault();
    const trimmedOtp = otp.replace(/\s/g, '');
    if (trimmedOtp.length !== 6) { setOtpError('Please enter the complete 6-digit code'); return; }
    if (!/^\d{6}$/.test(trimmedOtp)) { setOtpError('OTP must contain only digits'); return; }

    setIsLoading(true);
    setOtpError('');
    setSubmitError('');
    setSuccessMessage('');

    try {
      const { data, error } = await supabase.auth.verifyOtp({
        email: resolvedEmail,
        token: trimmedOtp,
        type: 'email',
      });

      if (error) {
        if (error.message?.toLowerCase().includes('expired')) {
          setOtpError('This code has expired. Please request a new one.');
        } else if (error.message?.toLowerCase().includes('invalid')) {
          setOtpError('Invalid code. Please check and try again.');
        } else {
          setOtpError(error.message || 'Verification failed. Please try again.');
        }
        return;
      }

      if (!data?.session) {
        setSubmitError('Verification failed. Please request a new code.');
        return;
      }

      // ── Role-based redirect ──────────────────────────────
      // Always check the user's role BEFORE redirecting.
      // Without this, an admin who lands on this page gets a valid
      // Supabase session, the app's route guards detect admin role,
      // and they get bounced to the admin console unexpectedly.
      setSuccessMessage('Login successful! Redirecting…');

      try {
        const userId = data.session.user?.id;
        let role = 'user'; // safe default

        if (userId) {
          const { data: profileData } = await supabase
            .from('user_profiles')
            .select('role')
            .eq('id', userId)
            .maybeSingle();

          role = profileData?.role?.toLowerCase()?.trim() || 'user';
        }

        setTimeout(() => {
          if (role === 'admin' || role === 'superadmin') {
            // Admin using the user login page — redirect to admin console
            window.location.href = '/admin-login-screen';
          } else {
            // Normal user — go to dashboard
            window.location.href = '/user-dashboard';
          }
        }, 1000);

      } catch (_) {
        // Profile lookup failed — fall back safely to user dashboard
        setTimeout(() => { window.location.href = '/user-dashboard'; }, 1000);
      }

    } catch (err) {
      setSubmitError(err?.message || 'Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // ── Resend OTP ────────────────────────────────────────────
  const handleResend = async () => {
    if (timeLeft > 0) return;
    setOtp('');
    setOtpError('');
    setSubmitError('');
    setSuccessMessage('');
    setIsLoading(true);
    try {
      const { error } = await supabase.auth.signInWithOtp({
        email: resolvedEmail,
        options: { shouldCreateUser: false },
      });
      if (error) { setSubmitError(error.message || 'Failed to resend. Try again.'); return; }
      startCountdown();
      setSuccessMessage(`Code resent to ${maskedEmail}`);
    } catch (err) {
      setSubmitError(err?.message || 'Something went wrong.');
    } finally {
      setIsLoading(false);
    }
  };

  // ── Back to step 1 ────────────────────────────────────────
  const handleBack = () => {
    setStep('input');
    setOtp('');
    setOtpError('');
    setSubmitError('');
    setSuccessMessage('');
    setResolvedEmail('');
    setMaskedEmail('');
  };

  // ─── Render ───────────────────────────────────────────────
  return (
    <>
      <style>{`
        @font-face {
          font-family: 'Sansation';
          src: local('Sansation Regular'), local('Sansation'), local('Sansation-Regular');
          font-weight: 400;
          font-style: normal;
        }
        @font-face {
          font-family: 'Sansation';
          src: local('Sansation Bold'), local('Sansation-Bold');
          font-weight: 700;
          font-style: normal;
        }
        @font-face {
          font-family: 'Sansation';
          src: local('Sansation Light'), local('Sansation-Light');
          font-weight: 300;
          font-style: normal;
        }

        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

        /* ── Headings use Sansation; everything else inherits ── */
        .login-root h1, .login-root h2, .login-root h3 {
          font-family: 'Sansation', sans-serif;
        }

        /* ── Root ────────────────────────────────────────────── */
        .login-root {
          min-height: 100dvh;
          width: 100%;
          background-image: url('/assets/images/Join 1000+ Founders Login.svg');
          background-size: cover;
          background-position: top left;
          background-repeat: no-repeat;
          display: flex;
          align-items: center;
          justify-content: flex-end;
          padding: 24px 5% 24px 0;
          font-family: inherit;
        }

        /* ── Card ────────────────────────────────────────────── */
        .login-card {
          width: min(460px, 100%);
          background: rgba(255,255,255,0.97);
          backdrop-filter: blur(20px);
          -webkit-backdrop-filter: blur(20px);
          border-radius: 20px;
          padding: 52px 44px 44px;
          box-shadow: 0 32px 80px rgba(0,0,0,0.22), 0 8px 24px rgba(0,0,0,0.10);
          border: 1px solid rgba(255,255,255,0.6);
          display: flex;
          flex-direction: column;
          gap: 0;
        }

        /* ── Mobile hero (hidden on desktop) ─────────────────── */
        .login-mobile-hero {
          display: none;
        }

        /* ── Primary button ──────────────────────────────────── */
        .login-btn {
          width: 100%;
          padding: 13px;
          border-radius: 10px;
          border: none;
          background: linear-gradient(135deg, #090A0B 0%, #001a4d 40%, #004AAD 75%, #0082FB 100%);
          color: #fff;
          font-weight: 600;
          font-size: 15px;
          font-family: inherit;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          box-shadow: 0 4px 16px rgba(0,74,173,0.35);
          cursor: pointer;
          transition: opacity 0.2s, transform 0.15s;
          -webkit-tap-highlight-color: transparent;
          touch-action: manipulation;
        }
        .login-btn:hover:not(:disabled) { opacity: 0.92; transform: translateY(-1px); }
        .login-btn:active:not(:disabled) { opacity: 0.85; transform: translateY(0); }
        .login-btn:disabled { background: #93AEED; cursor: not-allowed; box-shadow: none; }

        /* ── Identifier input ────────────────────────────────── */
        .id-input {
          width: 100%;
          padding: 11px 14px;
          border: 1.5px solid #E5E7EB;
          border-radius: 10px;
          font-size: 16px; /* prevents iOS auto-zoom */
          font-family: inherit;
          color: #0F172A;
          background: #F9FAFB;
          outline: none;
          transition: border-color 0.15s, box-shadow 0.15s;
          -webkit-text-size-adjust: 100%;
        }
        .id-input:focus {
          border-color: #3B6BE4;
          box-shadow: 0 0 0 3px rgba(59,107,228,0.10);
          background: #fff;
        }
        .id-input.error { border-color: #EF4444; }
        .id-input:disabled { background: #F3F4F6; color: #9CA3AF; cursor: not-allowed; }

        /* ── OTP boxes ───────────────────────────────────────── */
        .otp-box {
          width: 44px;
          height: 52px;
          text-align: center;
          font-size: 20px;
          font-weight: 700;
          border-radius: 10px;
          outline: none;
          color: #0F172A;
          transition: border-color 0.15s, background 0.15s;
          cursor: text;
          font-family: inherit;
          -webkit-tap-highlight-color: transparent;
        }
        .otp-box:disabled { cursor: not-allowed; opacity: 0.6; }

        /* ── Animations ──────────────────────────────────────── */
        @keyframes spin { to { transform: rotate(360deg); } }
        .spin { animation: spin 0.8s linear infinite; }

        @keyframes fadeSlide {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .fade-slide { animation: fadeSlide 0.3s ease; }

        /* ══════════════════════════════════════════════════════
           RESPONSIVE BREAKPOINTS
           ══════════════════════════════════════════════════════ */

        /* ── Large tablet / small laptop (≤ 900px) ───────────── */
        @media (max-width: 900px) {
          .login-root {
            justify-content: center;
            padding: 24px 16px;
          }
          .login-card {
            width: min(480px, 100%);
          }
        }

        /* ── Tablet (601px – 768px) ──────────────────────────── */
        @media (min-width: 601px) and (max-width: 768px) {
          .login-root {
            align-items: flex-start;
            padding: 16px;
          }
          .login-card {
            width: 100%;
            max-width: 480px;
            margin: 0 auto;
            padding: 36px 32px 28px;
          }
        }

        /* ── Mobile (≤ 600px) ────────────────────────────────── */
        @media (max-width: 600px) {
          /* Remove fullscreen bg — use compact hero strip instead */
          .login-root {
            background-image: none;
            background-color: #f3f4f6;
            align-items: stretch;
            justify-content: flex-start;
            padding: 0;
            flex-direction: column;
            overflow-y: auto;
            min-height: 100dvh;
            width: 100%;
          }

          /* Compact image strip at the top */
          .login-mobile-hero {
            display: block;
            width: 100%;
            height: min(180px, 35vh);
            background-image: url('/assets/images/Join 1000+ Founders Login.svg');
            background-size: cover;
            background-position: center 20%;
            background-repeat: no-repeat;
            flex-shrink: 0;
          }

          /* Card: full-width, overlaps hero strip by 16px */
          .login-card {
            width: 100% !important;
            max-width: 100% !important;
            margin: -16px 0 0 0 !important;
            border-radius: 16px 16px 0 0;
            padding: 28px 20px 32px;
            padding-bottom: max(32px, env(safe-area-inset-bottom));
            box-shadow: 0 -4px 20px rgba(0,0,0,0.10);
            border: none;
            background: #fff;
            flex: 1;
            overflow-y: visible;
            position: relative;
            z-index: 1;
          }

          .login-btn { font-size: 14px; }

          /* OTP boxes slightly smaller on mobile */
          .otp-box {
            width: 40px;
            height: 48px;
            font-size: 18px;
          }
        }

        /* ── Very small phones (≤ 375px) ─────────────────────── */
        @media (max-width: 375px) {
          .login-mobile-hero {
            height: min(160px, 30vh);
          }
          .login-card {
            padding: 22px 16px 28px !important;
            padding-bottom: max(28px, env(safe-area-inset-bottom)) !important;
          }
          .otp-box {
            width: 36px;
            height: 44px;
            font-size: 16px;
            border-radius: 8px;
          }
        }
      `}</style>

      <div className="login-root">
        {/* Mobile-only compact hero image strip */}
        <div className="login-mobile-hero" aria-hidden="true" />

        <div className="login-card">

          {/* ── Logo ── */}
          <div style={{ marginBottom: '20px' }}>
            <img
              src="/assets/images/8.svg"
              alt="DecX AI"
              style={{ height: '36px', width: 'auto', objectFit: 'contain', display: 'block', marginBottom: '12px' }}
            />
            <p style={{ fontSize: '13px', color: '#6B7280', margin: 0 }}>
              Welcome to <strong style={{ color: '#111827' }}>DecX AI</strong>
            </p>
            <h1 style={{ fontSize: 'clamp(18px, 4vw, 22px)', fontWeight: '700', color: '#0F172A', lineHeight: '1.3', margin: '4px 0 0' }}>
              {step === 'input' ? 'Sign in to your account' : 'Enter your code'}
            </h1>
          </div>

          {/* ── Success Banner ── */}
          {successMessage && (
            <div className="fade-slide" style={{ background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: '10px', padding: '11px 14px', display: 'flex', gap: '10px', alignItems: 'flex-start', marginBottom: '16px' }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#16A34A" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: '1px' }}>
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>
              </svg>
              <p style={{ fontSize: '13px', color: '#15803D', margin: 0 }}>{successMessage}</p>
            </div>
          )}

          {/* ── Error Banner ── */}
          {submitError && (
            <div className="fade-slide" style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: '10px', padding: '11px 14px', display: 'flex', gap: '10px', alignItems: 'flex-start', marginBottom: '16px' }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#EF4444" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: '1px' }}>
                <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
              <p style={{ fontSize: '13px', color: '#DC2626', margin: 0 }}>{submitError}</p>
            </div>
          )}

          {/* ════════════ STEP 1: INPUT ════════════ */}
          {step === 'input' && (
            <form
              onSubmit={handleSendOtp}
              noValidate
              style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}
              className="fade-slide"
            >
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>
                  Email or Mobile <span style={{ color: '#EF4444' }}>*</span>
                </label>
                <input
                  type="text"
                  placeholder="you@company.com or 9876543210"
                  value={identifier}
                  onChange={handleIdentifierChange}
                  onBlur={handleIdentifierBlur}
                  disabled={isLoading}
                  autoComplete="email"
                  autoFocus
                  className={`id-input${identifierTouched && identifierError ? ' error' : ''}`}
                />
                {identifierTouched && identifierError && (
                  <p style={{ fontSize: '12px', color: '#DC2626', marginTop: '5px', marginBottom: 0 }}>
                    {identifierError}
                  </p>
                )}
                <p style={{ fontSize: '12px', color: '#6B7280', marginTop: '6px', marginBottom: 0 }}>
                  OTP will be sent to your registered email address.
                </p>
              </div>

              <button type="submit" disabled={isLoading} className="login-btn">
                {isLoading ? (
                  <>
                    <svg className="spin" width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" opacity="0.25"/>
                      <path fill="currentColor" opacity="0.75" d="M4 12a8 8 0 018-8v8z"/>
                    </svg>
                    {isMobileNum(identifier) ? 'Looking up account…' : 'Sending Code…'}
                  </>
                ) : (
                  <>
                    Send Code
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/>
                    </svg>
                  </>
                )}
              </button>
            </form>
          )}

          {/* ════════════ STEP 2: OTP ════════════ */}
          {step === 'otp' && (
            <form
              onSubmit={handleVerifyOtp}
              noValidate
              style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}
              className="fade-slide"
            >
              {/* Sent-to email + change button */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', background: '#F8FAFF', border: '1px solid #DBEAFE', borderRadius: '10px', padding: '10px 14px' }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#3B6BE4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                  <polyline points="22,6 12,13 2,6"/>
                </svg>
                <span style={{ fontSize: '13px', color: '#1E3FA8', fontWeight: '500', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {maskedEmail}
                </span>
                <button
                  type="button"
                  onClick={handleBack}
                  disabled={isLoading}
                  style={{ background: 'none', border: 'none', color: '#3B6BE4', fontSize: '13px', fontWeight: '600', cursor: 'pointer', padding: 0, flexShrink: 0, fontFamily: 'inherit' }}
                >
                  Change
                </button>
              </div>

              {/* OTP Boxes */}
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '12px', textAlign: 'center' }}>
                  Enter the 6-digit code
                </label>
                <OtpInput
                  value={otp}
                  onChange={(val) => { setOtp(val); setOtpError(''); }}
                  disabled={isLoading}
                />
                {otpError && (
                  <p style={{ fontSize: '12px', color: '#DC2626', textAlign: 'center', marginTop: '8px', marginBottom: 0 }}>
                    {otpError}
                  </p>
                )}
              </div>

              <button type="submit" disabled={isLoading} className="login-btn">
                {isLoading ? (
                  <>
                    <svg className="spin" width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" opacity="0.25"/>
                      <path fill="currentColor" opacity="0.75" d="M4 12a8 8 0 018-8v8z"/>
                    </svg>
                    Verifying…
                  </>
                ) : (
                  <>
                    Verify & Sign In
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                  </>
                )}
              </button>

              {/* Resend */}
              <p style={{ textAlign: 'center', fontSize: '13px', color: '#6B7280', margin: 0 }}>
                Didn't receive it?{' '}
                {timeLeft > 0 ? (
                  <span style={{ color: '#9CA3AF' }}>Resend in {timeLeft}s</span>
                ) : (
                  <button
                    type="button"
                    onClick={handleResend}
                    disabled={isLoading}
                    style={{ background: 'none', border: 'none', color: '#3B6BE4', fontWeight: '600', fontSize: '13px', cursor: 'pointer', padding: 0, fontFamily: 'inherit' }}
                  >
                    Resend Code
                  </button>
                )}
              </p>
            </form>
          )}

          {/* ── Create Account link ── */}
          <p style={{ textAlign: 'center', fontSize: '13px', color: '#6B7280', marginTop: '20px', marginBottom: 0 }}>
            Don't have an account?{' '}
            <Link to="/registration-screen" style={{ color: '#3B6BE4', fontWeight: '600', textDecoration: 'none' }}>
              Create Account
            </Link>
          </p>

          {/* ── Footer ── */}
          <div style={{ borderTop: '1px solid #F3F4F6', marginTop: '16px', paddingTop: '12px' }}>
            <p style={{ textAlign: 'center', fontSize: '11px', color: '#9CA3AF', margin: 0 }}>
              By continuing you agree to our{' '}
              <a href="https://decx.ai/#/privacy" style={{ color: '#3B6BE4', textDecoration: 'underline' }}>privacy policy</a>
              {' '}and{' '}
              <a href="https://decx.ai/#/terms" style={{ color: '#3B6BE4', textDecoration: 'underline' }}>terms of use</a>
            </p>
          </div>

        </div>
      </div>
    </>
  );
};

export default LoginScreen;